/**
 * Fetches the current Shopify cart and searches for an guarantee item.
 *
 * @async
 * @function
 * @returns {Promise<Object|undefined>} A promise that resolves to the guarantee cart item object if found, or undefined if not found.
 */
async function ca_fetchGuarantee() {
  // Fetch the cart
  const cartResponse = await fetch('/cart.js');
  const cart = await cartResponse.json();

  // Search for guarantee cart item
  return cart.items.find((item) => item.product_type === 'guarantee' || item.handle === 'guarantee');
}

/**
 * Removes the guarantee item from the cart if it exists.
 *
 * This function checks for an existing guarantee item in the cart using `ca_fetchGuarantee`.
 * If found, it sends a POST request to remove the item by setting its quantity to 0.
 *
 * @async
 * @function ca_removeGuarantee
 * @returns {Promise<boolean>} Returns `true` if guarantee was removed, `false` if not found.
 */
async function ca_removeGuarantee() {
  const existingGuarantee = await ca_fetchGuarantee();

  if (existingGuarantee) {
    await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        id: existingGuarantee.key,
        quantity: 0,
      }),
    });

    return true;
  }

  return false;
}

/**
 * Replaces the guarantee item in the cart with a new variant.
 *
 * This function first removes any existing guarantee item from the cart using `ca_removeGuarantee`.
 * Then, it adds the new guarantee variant to the cart by sending a POST request to `/cart/add.js`.
 *
 * @async
 * @function ca_replaceGuarantee
 * @param {number} variantId - The ID of the guarantee variant to add.
 * @returns {Promise<void>}
 */
async function ca_replaceGuarantee(variantId) {
  await ca_removeGuarantee();

  // Add the new guarantee item
  await fetch('/cart/add.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      items: [
        {
          id: variantId,
          quantity: 1,
        },
      ],
    }),
  });
}

/**
 * Computes the total declared value of items in the Shopify cart.
 * Each item's declared value (in cents) is multiplied by its quantity and summed.
 * Fetches the cart data from the Shopify store.
 *
 * @async
 * @function ca_computeDeclaredTotal
 * @returns {Promise<number>} The total declared value in cents.
 */
async function ca_computeDeclaredTotal() {
  // Fetch the cart
  const cartResponse = await fetch('/cart.js');
  const cart = await cartResponse.json();

  // Compute declared total
  return cart.items.reduce((accumulator, item) => {
    if (item && item.properties && item.properties._declared_value) {
      accumulator += item.properties._declared_value * item.quantity;
    }
    return accumulator;
  }, 0);
}

/**
 * Determines the appropriate guarantee variant based on the declared total in Euros.
 *
 * Iterates through the available guarantee variants and returns the one whose
 * minimum and maximum coverage range includes the declared total.
 *
 * @async
 * @function ca_determineGuaranteeVariant
 * @returns {Promise<Object|null>} The matching guarantee variant object, or null if none found or if guaranteeVariants is not defined.
 */
async function ca_determineGuaranteeVariant() {
  if (!window.guaranteeVariants) {
    console.error('No guarantee variants are available');
    return null;
  }

  // As declared total is returned in cents, we want to in Euros
  const declaredTotal = await ca_computeDeclaredTotal();

  for (let variant of window.guaranteeVariants) {
    if (declaredTotal >= variant.min && declaredTotal <= variant.max) {
      return variant;
    }
  }

  return null;
}

async function ca_computeGuarantee(ensureGuaranteeInCart = false) {
  let existingGuarantee = null;

  if (ensureGuaranteeInCart) {
    existingGuarantee = await ca_fetchGuarantee();
    if (!existingGuarantee) {
      // In case there is no guarantee in the cart there is no need to continue here
      return;
    }
  }

  const guaranteeVariant = await ca_determineGuaranteeVariant();
  if (existingGuarantee && existingGuarantee.id == guaranteeVariant.id) {
    // If the right guarantee is already in the cart, no need to take action
    return;
  }

  await ca_replaceGuarantee(guaranteeVariant.id);
}
